import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconDeviceFloppy, IconRestore } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  ApiError,
  listResourceUnits,
  updateResourceUnit,
  type ResourceUnitSummary,
  type Zone,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly zone: Zone;
  readonly unitLabel?: string;
  readonly onSaved: () => void;
}

interface RowDraft {
  readonly label: string;
  readonly capacity: number | '';
  readonly active: boolean;
  readonly minCapacity: number | '';
}

function toDraft(u: ResourceUnitSummary): RowDraft {
  return {
    label: u.label,
    capacity: u.capacity,
    active: u.active,
    minCapacity: u.minCapacity ?? '',
  };
}

function draftDiffers(u: ResourceUnitSummary, d: RowDraft): boolean {
  if (d.label !== u.label) return true;
  if (d.capacity !== u.capacity) return true;
  if (d.active !== u.active) return true;
  const dMin = d.minCapacity === '' ? null : d.minCapacity;
  if (dMin !== u.minCapacity) return true;
  return false;
}

export function ZoneUnitsModal({
  opened,
  onClose,
  zone,
  unitLabel = '格',
  onSaved,
}: Props): JSX.Element {
  const [units, setUnits] = useState<ResourceUnitSummary[]>([]);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setErr(null);
    try {
      const list = await listResourceUnits(zone.resourceId);
      setUnits(list);
      setDrafts(Object.fromEntries(list.map((u) => [u.unitId, toDraft(u)])));
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!opened) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, zone.resourceId]);

  function updateDraft(unitId: string, patch: Partial<RowDraft>): void {
    setDrafts((d) => ({ ...d, [unitId]: { ...d[unitId]!, ...patch } }));
  }

  async function handleSave(u: ResourceUnitSummary): Promise<void> {
    const d = drafts[u.unitId]!;
    if (d.label.trim().length === 0) {
      setErr(`${unitLabel} ${u.position}：名稱不可為空`);
      return;
    }
    if (typeof d.capacity !== 'number' || d.capacity < 1) {
      setErr(`${unitLabel} ${u.position}：容量必須 ≥ 1`);
      return;
    }
    const min = d.minCapacity === '' ? null : d.minCapacity;
    if (min !== null && (typeof min !== 'number' || min < 1 || min > d.capacity)) {
      setErr(`${unitLabel} ${u.position}：最小容量必須 1–${d.capacity} 或留空`);
      return;
    }
    setErr(null);
    setSaving(u.unitId);
    try {
      const updated = await updateResourceUnit(u.unitId, {
        label: d.label.trim(),
        capacity: d.capacity,
        active: d.active,
        minCapacity: min,
      });
      setUnits((list) => list.map((x) => (x.unitId === updated.unitId ? updated : x)));
      setDrafts((drafts0) => ({ ...drafts0, [updated.unitId]: toDraft(updated) }));
      notifications.show({
        color: 'green',
        title: `${unitLabel} ${updated.position} 已更新`,
        message: `${updated.label} · 容量 ${updated.capacity} · ${updated.active ? '啟用' : '停用'}`,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setSaving(null);
    }
  }

  function handleReset(u: ResourceUnitSummary): void {
    setDrafts((d) => ({ ...d, [u.unitId]: toDraft(u) }));
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${unitLabel}管理 · ${zone.zoneLabel}`}
      centered
      size="xl"
    >
      {loading && <Loader />}
      {err && (
        <Alert color="red" variant="light" mb="sm">
          {err}
        </Alert>
      )}
      {!loading && units.length === 0 && (
        <Text size="sm" c="dimmed">
          此區域尚未設置任何{unitLabel}
        </Text>
      )}
      {!loading && units.length > 0 && (
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            每列修改後按 💾 儲存；停用的{unitLabel}不會出現在新預訂的可用位置計算。
          </Text>
          <Table verticalSpacing="sm" striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 50 }}>#</Table.Th>
                <Table.Th>名稱</Table.Th>
                <Table.Th style={{ width: 90 }}>容量</Table.Th>
                <Table.Th style={{ width: 100 }}>最小容量</Table.Th>
                <Table.Th style={{ width: 80 }}>啟用</Table.Th>
                <Table.Th style={{ width: 90 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {units.map((u) => {
                const d = drafts[u.unitId]!;
                const dirty = draftDiffers(u, d);
                return (
                  <Table.Tr key={u.unitId}>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={tokens.color.mantine.primary}
                        radius="sm"
                      >
                        {u.position}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        value={d.label}
                        onChange={(e) => updateDraft(u.unitId, { label: e.currentTarget.value })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        value={d.capacity}
                        onChange={(v) =>
                          updateDraft(u.unitId, { capacity: typeof v === 'number' ? v : '' })
                        }
                        min={1}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        placeholder="空=不限"
                        value={d.minCapacity}
                        onChange={(v) =>
                          updateDraft(u.unitId, { minCapacity: typeof v === 'number' ? v : '' })
                        }
                        min={1}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={d.active}
                        onChange={(e) => updateDraft(u.unitId, { active: e.currentTarget.checked })}
                        color={tokens.color.mantine.primary}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="儲存">
                          <ActionIcon
                            color={tokens.color.mantine.primary}
                            variant="light"
                            loading={saving === u.unitId}
                            disabled={!dirty || saving !== null}
                            onClick={() => void handleSave(u)}
                          >
                            <IconDeviceFloppy size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="還原">
                          <ActionIcon
                            color="gray"
                            variant="subtle"
                            disabled={!dirty}
                            onClick={() => handleReset(u)}
                          >
                            <IconRestore size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Group justify="flex-end">
            <Button variant="light" onClick={onClose}>
              關閉
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
